import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';

import { DecisionRefusal } from '../DecisionRefusal/DecisionRefusal';

import {
  useOrderDecisionModalLogic,
  type ReasonValues,
  type ReasonedAction,
} from './useOrderDecisionModalLogic';

/**
 * Rejecting an order, or recording a refund — the two decisions that require a
 * reason.
 *
 * Presentation only; every decision (which copy, which mutation, what a refusal
 * says and what it offers) is in `useOrderDecisionModalLogic` (U5).
 *
 * The refusal is rendered as the form's `children`, which the kit places under
 * the fields and above the actions — i.e. **where the operator is looking**, and
 * with the dialog still open (P20b).
 */
export function OrderDecisionModal({ open, onCancel, modalData }: ModalComponentProps) {
  const view = useOrderDecisionModalLogic({
    orderId: typeof modalData?.['orderId'] === 'string' ? modalData['orderId'] : '',
    action: readAction(modalData?.['action']),
    onDone: onCancel,
  });

  return (
    <DynamicFormModal<ReasonValues>
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      title={view.title}
      description={view.description}
      size="md"
      isLoading={view.isLoading}
      fields={view.fields}
      schema={view.schema}
      initialValues={view.initialValues}
      submitLabel={view.submitLabel}
      onCancel={onCancel}
      onSubmit={view.submit}
    >
      <DecisionRefusal
        message={view.error}
        isConflict={view.isConflict}
        isGone={view.isGone}
        onReload={view.reload}
        onClose={onCancel}
      />
    </DynamicFormModal>
  );
}

/**
 * `ModalData` is untyped by design (it crosses the store), so the action is
 * narrowed rather than asserted (U4). Anything else falls back to the reversible
 * one: a payload the dialog cannot read must never open the act that revokes a
 * member's subscription.
 */
function readAction(value: unknown): ReasonedAction {
  return value === 'refund' ? 'refund' : 'reject';
}
